import React, { Component } from 'react';
import ReactTooltip from 'react-tooltip';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import './ag-theme-ela.css';

import * as CellRenderers from './AgGridCellRendererComponents';
import { FetchData } from './utils/DataFetch';
import { GetCookie } from './utils/Common';

const moment = require('moment');

const formatHistoryDate = (value) =>
  value ? moment(value).format('MM/DD/YYYY hh:mm A') : '';

const normalizeType = (val) => (val || '').toString().trim().toLowerCase();
const allowedHistoryTypes = new Set([
  'job note',
  'special billing',
  'int finish',
  'intfee',
  'int fee',
  'covering email',
  'billing auto',
  'follow up',
  'followup',
  'follow-up',
]);

const groupJobHistoryByJobId = (historyList) => {
  const cache = {};
  const arr = Array.isArray(historyList) ? historyList : [];
  arr.forEach((entry) => {
    const jobId = entry?.jobId ?? entry?.JobId;
    if (!jobId) return;
    if (!cache[jobId]) cache[jobId] = [];
    cache[jobId].push(entry);
  });
  return cache;
};

const JobHistoryDetailRenderer = (props) => {
  const detail = props.data || {};
  const jobHistory = Array.isArray(detail.jobHistory) ? detail.jobHistory : [];
  const loading = detail.loading;
  const filteredHistory = jobHistory.filter((entry) =>
    allowedHistoryTypes.has(normalizeType(entry.jobHistoryType))
  );

  const containerStyle = {
    padding: '0 1px',
    fontSize: '11px',
    background: '#dbeeff',
    border: '1px solid #9bbce6',
    borderRadius: '2px',
    boxShadow: 'inset 0 1px 0 #c9ddf7',
  };

  const renderMessage = (message) => (
    <div style={containerStyle}>{message}</div>
  );

  const openFollowUpJob = (jobNum, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const handler = props?.context?.componentParent?.handleOpenFollowUpJob;
    if (typeof handler === 'function') {
      handler(jobNum);
    }
  };

  const renderFollowUpDetail = (entry) => {
    const desc = entry.description || '';
    const match = desc.match(/^\s*\[\s*([^\]]+)\s*\]\s*(.*)$/);
    if (!match) return desc;

    const jobNum = match[1]?.trim();
    const rest = match[2] || '';

    return (
      <>
        <button
          type="button"
          onClick={(e) => openFollowUpJob(jobNum, e)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: 'blue',
            textDecoration: 'underline',
            cursor: 'pointer',
            font: 'inherit',
          }}
        >
          [{jobNum}]
        </button>
        {rest}
      </>
    );
  };

  const renderDetailCell = (entry) => {
    if (normalizeType(entry.jobHistoryType) === 'follow up') {
      return renderFollowUpDetail(entry);
    }
    return entry.description || '';
  };

  if (loading) return renderMessage('Loading job history...');
  if (!filteredHistory.length)
    return renderMessage('No job history found for this job.');

  return (
    <div style={containerStyle}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
        }}
      >
        <thead>
          <tr
            style={{
              background: '#b8d8ff',
              borderBottom: '1px solid #9bbce6',
              color: '#083b72',
            }}
          >
            <th
              style={{
                padding: '0 2px',
                textAlign: 'left',
                width: '160px',
                lineHeight: '16px',
              }}
            >
              Created On
            </th>
            <th
              style={{
                padding: '0 2px',
                textAlign: 'left',
                width: '150px',
                lineHeight: '16px',
              }}
            >
              Created By
            </th>
            <th
              style={{
                padding: '0 2px',
                textAlign: 'left',
                width: '140px',
                lineHeight: '16px',
              }}
            >
              Type
            </th>
            <th
              style={{
                padding: '0 2px',
                textAlign: 'left',
                lineHeight: '16px',
              }}
            >
              Detail
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredHistory.map((entry, index) => (
            <tr
              key={entry.jobHistoryId || index}
              style={{
                background: (() => {
                  const type = normalizeType(entry.jobHistoryType);
                  if (type === 'special billing') return '#f3a6ae';
                  if (type === 'follow up') return '#fff3b0';
                  return '#ffffff';
                })(),
                color: (() => {
                  const type = normalizeType(entry.jobHistoryType);
                  if (type === 'special billing') return '#5c1114';
                  if (type === 'follow up') return '#7a5b00';
                  return undefined;
                })(),
                border: '1px solid #b0b0b0',
              }}
            >
              <td
                style={{
                  padding: '0 2px',
                  verticalAlign: 'top',
                  lineHeight: '16px',
                }}
              >
                {formatHistoryDate(entry.createDateTime)}
              </td>
              <td
                style={{
                  padding: '0 2px',
                  verticalAlign: 'top',
                  lineHeight: '16px',
                }}
              >
                {entry.createdBy || ''}
              </td>
              <td
                style={{
                  padding: '0 2px',
                  verticalAlign: 'top',
                  lineHeight: '16px',
                }}
              >
                {entry.jobHistoryType || ''}
              </td>
              <td
                style={{
                  padding: '0 2px',
                  whiteSpace: 'pre-wrap',
                  verticalAlign: 'top',
                  lineHeight: '16px',
                }}
              >
                {renderDetailCell(entry)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DEFAULT_MIN_WIDTH_CELL = 10;
const DEFAULT_MAX_WIDTH_CELL = 800;

const defaultColDef = {
  resizable: true,
  sortable: true,
  filter: true,
  minWidth: DEFAULT_MIN_WIDTH_CELL,
  maxWidth: DEFAULT_MAX_WIDTH_CELL,
  cellStyle: {
    borderRight: '1px solid #ccc', // Adds a light gray divider
  },
};

const columns = [
  {
    headerName: '',
    colId: 'select',
    width: 30,
    headerCheckboxSelection: true,
    checkboxSelection: (params) => !params.data?.__isDetail,
    sortable: false,
    filter: false,
    autoHeight: true,
    colSpan: (params) =>
      params.data && params.data.__isDetail ? columns.length : 1,
    cellRendererSelector: (params) => {
      if (params.data && params.data.__isDetail) {
        return { component: 'jobHistoryDetailRenderer' };
      }
      return undefined;
    },
    cellStyle: (params) =>
      params.data && params.data.__isDetail
        ? { padding: 0 }
        : {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
  },
  {
    headerName: '',
    field: 'repEmail2',
    cellRenderer: 'repEmail2CellRenderer',
    width: 30,
    sortable: false,
    filter: false,
  },
  {
    headerName: 'Job#',
    field: 'jobNum',
    cellRenderer: 'jobNumCellRenderer',
    cellRendererParams: { context: { componentParent: this } },
    width: 65,
  },
  {
    headerName: '',
    field: 'addFollowUp',
    cellRenderer: 'addFollowUpCellRenderer',
    cellRendererParams: { context: { componentParent: this } },
    width: 30,
    sortable: false,
    filter: false,
  },
  {
    headerName: 'Prov Conf',
    field: 'provConfFlg',
    cellRenderer: 'profConfCellRenderer',
    width: 60,
  },
  {
    headerName: 'Rep Auth',
    field: 'repAuthorizedFlg',
    cellRenderer: 'repAuthorizedCellRenderer',
    width: 60,
  },
  {
    headerName: '',
    field: 'transportationFlg',
    cellRenderer: 'transportationCellRenderer',
    width: 20,
  },
  {
    headerName: 'Date',
    field: 'jobDateTime',
    cellRenderer: 'jobDateCellRenderer',
    tooltipValueGetter: function (params) {
      const { data } = params;
      const jobDate = data.jobDateTime
        ? moment(data.jobDateTime).format('MM/DD/YYYY hh:mm A')
        : '';
      const finishTime = data.finishTimeString
        ? ' to ' + data.finishTimeString
        : '';
      const tooltipContent = `CreatedBy: ${
        data.createdBy
      }\nCreated On: ${moment(data.createDateTime).format(
        'MM/DD/YYYY hh:mm A'
      )}\nUpdated By: ${data.updatedBy}\nUpdated On: ${moment(
        data.updateDateTime
      ).format('MM/DD/YYYY hh:mm A')}`;
      return `${jobDate} ${finishTime}\n${tooltipContent}`;
    },
    width: 125,
  },
  {
    headerName: 'Claimant',
    field: 'clmLastName',
    cellRenderer: 'claimantNameCellRenderer',
    tooltipValueGetter: function (params) {
      const { data } = params;
      const claimantNotes = data.clmNote === null ? '' : data.clmNote;
      const tooltipContent = `${claimantNotes}`;
      return tooltipContent;
    },
    width: 125,
  },
  {
    headerName: '',
    field: 'jobNotes',
    cellRenderer: 'jobNotesCellRenderer',
    width: 20,
    sortable: false,
    filter: false,
    cellStyle: { paddingLeft: '0px' },
  },
  { headerName: 'CLM#', field: 'clmClaimNumber', width: 105 },
  { headerName: 'CLM Ph', field: 'clmPhone', width: 60 },
  {
    headerName: '',
    field: '',
    cellRenderer: 'intEmailCellRenderer',
    width: 30,
    sortable: false,
    filter: false,
  },
  {
    headerName: 'Interpreter',
    field: 'intLastName',
    cellRenderer: 'intNameCellRenderer',
    width: 125,
  },
  { headerName: 'Int Cell /Text', field: 'intCellTxt', width: 80 },
  {
    headerName: '',
    cellRenderer: 'providerEmailCellRenderer',
    width: 30,
    sortable: false,
    filter: false,
  },
  { headerName: 'Job Provider', field: 'providerName', width: 155 },
  {
    headerName: '',
    field: 'repEmail',
    cellRenderer: 'repEmailCellRenderer',
    width: 30,
    sortable: false,
    filter: false,
  },
  {
    headerName: 'Rep',
    field: 'repLastName',
    cellRenderer: 'repNameCellRenderer',
    width: 125,
  },
  { headerName: 'Rep Ph', field: 'repPhone', width: 105 },
  { headerName: 'Client Ph', field: 'clientPhone', width: 80 },
  { headerName: 'Client', field: 'clientAbbrv', width: 68 },
  {
    headerName: '',
    field: 'invoicedFlg',
    cellRenderer: 'invoicedFlgCellRenderer',
    width: 20,
  },
  { headerName: 'Caller Notes', field: 'callerNote', width: 125 },
];

export class WorkOrderTablePurpleReport extends Component {
  constructor(props) {
    super(props);
    this.gridApi = null;
    this.gridColumnApi = null;
    this.previousSelection = [];
    this.singleClickTimer = null;
    this.isUnmounted = false;
    try {
      this.user = JSON.parse(GetCookie('user') || '{}');
    } catch (err) {
      this.user = {};
    }
    this.state = {
      columnWidths: columns.reduce((acc, col) => {
        acc[col.field] = col.width || DEFAULT_MIN_WIDTH_CELL; // Initialize widths
        return acc;
      }, {}),
      isResizing: -1,
      expandedJobIds: [],
      jobHistoryCache: groupJobHistoryByJobId(props.jobHistoryList),
      loadingJobIds: [],
    };

    this.onSelectionChanged = this.onSelectionChanged.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.autoSizeUnspecifiedColumns =
      this.autoSizeUnspecifiedColumns.bind(this);
    this.adjustHeaderHeight = this.adjustHeaderHeight.bind(this);
    this.handleRowDoubleClick = this.handleRowDoubleClick.bind(this);
    this.onSortChanged = this.onSortChanged.bind(this);
    this.hasRelevantHistory = this.hasRelevantHistory.bind(this);
  }

  buildJobHistoryCacheFromProps = (props) =>
    groupJobHistoryByJobId(props?.jobHistoryList);

  syncJobHistoryCacheFromProps = (props, callback) => {
    const nextCache = this.buildJobHistoryCacheFromProps(props);
    this.setState(
      {
        jobHistoryCache: nextCache,
        loadingJobIds: [],
      },
      callback
    );
  };

  componentDidMount() {
    ReactTooltip.rebuild();
    this.syncJobHistoryCacheFromProps(this.props, () => {
      this.expandAllRows();
      if (typeof this.props.onJobHistoryLoadingChange === 'function') {
        this.props.onJobHistoryLoadingChange(false);
      }
    });
  }

  getSnapshotBeforeUpdate(prevProps) {
    if (prevProps.workOrders !== this.props.workOrders && this.gridApi) {
      return this.gridApi.getFilterModel();
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.clearSelection && !prevProps.clearSelection) {
      if (this.gridApi) {
        this.gridApi.deselectAll();
      }
    }
    const workOrdersChanged = prevProps.workOrders !== this.props.workOrders;
    const jobHistoryChanged =
      prevProps.jobHistoryList !== this.props.jobHistoryList;

    if (jobHistoryChanged) {
      this.syncJobHistoryCacheFromProps(this.props, () => {
        this.expandAllRows();
      });
    } else if (workOrdersChanged) {
      this.expandAllRows();
    }

    const prevLoading = (prevState.loadingJobIds || []).length > 0;
    const currLoading = (this.state.loadingJobIds || []).length > 0;
    if (
      prevLoading !== currLoading &&
      typeof this.props.onJobHistoryLoadingChange === 'function'
    ) {
      this.props.onJobHistoryLoadingChange(currLoading);
    }

    if (snapshot !== null && this.gridApi) {
      this.gridApi.setFilterModel(snapshot);
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    if (this.singleClickTimer) {
      clearTimeout(this.singleClickTimer);
    }
    if (typeof this.props.onJobHistoryLoadingChange === 'function') {
      this.props.onJobHistoryLoadingChange(false);
    }
  }

  onSelectionChanged = () => {
    if (!this.gridApi) return;

    const currentSelection = this.gridApi.getSelectedRows();
    const currentSelectionIds = currentSelection.map((item) => item.jobId);
    const previousSelectionIds = this.previousSelection.map(
      (item) => item.jobId
    );

    const newlySelected = currentSelection.filter(
      (node) => !previousSelectionIds.includes(node.jobId)
    );
    const newlyDeselected = this.previousSelection.filter(
      (node) => !currentSelectionIds.includes(node.jobId)
    );

    newlySelected.forEach((node) => {
      const syntheticEvent = {
        target: {
          name: 'chk_' + node.jobNum,
          checked: true,
        },
      };
      this.props.handleCheckChange(syntheticEvent);
    });

    newlyDeselected.forEach((node) => {
      const syntheticEvent = {
        target: {
          name: 'chk_' + node.jobNum,
          checked: false,
        },
      };
      this.props.handleCheckChange(syntheticEvent);
    });

    this.previousSelection = [...currentSelection];
  };

  onGridReady = (params) => {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    if (this.props.onGridReady) {
      this.props.onGridReady(params);
    }
    this.adjustHeaderHeight(params.api);
    this.autoSizeUnspecifiedColumns();
  };

  autoSizeUnspecifiedColumns = () => {
    if (!this.gridColumnApi) return;

    const allColumnIds = [];
    this.gridColumnApi.getColumns().forEach((column) => {
      if (column.getColDef().width === undefined) {
        allColumnIds.push(column.colId);
      }
    });
    if (allColumnIds.length > 0) {
      this.gridColumnApi.autoSizeColumns(allColumnIds, false);
    }
  };

  adjustHeaderHeight = (gridApi) => {
    window.requestAnimationFrame(() => {
      const allColumnHeaders = document.querySelectorAll(
        '.ag-header-cell-text'
      );
      let maxHeight = 0;
      allColumnHeaders.forEach((header) => {
        if (header.parentElement.clientHeight > maxHeight) {
          maxHeight = header.parentElement.clientHeight;
        }
      });
      maxHeight += 10;
      gridApi.setHeaderHeight(maxHeight);
    });
  };

  handleRowDoubleClick = (event) => {
    if (this.singleClickTimer) {
      clearTimeout(this.singleClickTimer);
    }
    this.openWorkOrder(event?.data, event?.rowIndex);
  };

  handleJobNumClick = (workorder, rowIndex) => {
    this.openWorkOrder(workorder, rowIndex);
  };

  handleOpenFollowUpJob = async (jobNum) => {
    const normalizedJobNum = (jobNum || '').toString().trim();
    if (!normalizedJobNum || !this.user?.token) return;

    try {
      const data = await FetchData(
        '/getworkorders',
        'POST',
        this.user.token,
        { JobNum: normalizedJobNum, StartDate: null, EndDate: null }
      );
      const jobs = Array.isArray(data) ? data : [];
      const targetJob = jobs.find(
        (job) =>
          String(job.jobNum || job.JobNum || job.jobnum || '').trim() ===
          normalizedJobNum
      );

      if (targetJob) {
        const rowIndex = Array.isArray(this.props.workOrders)
          ? this.props.workOrders.findIndex(
              (wo) =>
                String(wo.jobNum || wo.JobNum || wo.jobnum || '').trim() ===
                normalizedJobNum
            )
          : 0;

        this.openWorkOrder(targetJob, rowIndex >= 0 ? rowIndex : 0);
      }
    } catch (err) {
      // No-op; user can still open manually via other means.
    }
  };

  openWorkOrder = (workorder, rowIndex) => {
    if (!workorder || workorder.__isDetail) return;

    const providedIndex =
      typeof rowIndex === 'number' && rowIndex >= 0 ? rowIndex : 0;
    const baseIndex = Array.isArray(this.props.workOrders)
      ? this.props.workOrders.findIndex((wo) => wo.jobId === workorder.jobId)
      : providedIndex;
    const resolvedIndex =
      baseIndex === -1 || baseIndex === undefined ? providedIndex : baseIndex;

    this.props.setData(workorder, resolvedIndex, 'workorder');
  };

  handleRowSingleClick = (event) => {
    if (event?.data?.__isDetail) return;
    if (event?.event?.target?.type === 'checkbox') return;
    if (this.singleClickTimer) {
      clearTimeout(this.singleClickTimer);
    }

    this.singleClickTimer = setTimeout(() => {
      this.toggleJobHistory(event.data);
      if (typeof this.props.onRowClicked === 'function') {
        this.props.onRowClicked(event);
      }
    }, 200);
  };

  toggleJobHistory = (workorder) => {
    const jobId = workorder?.jobId;
    if (!jobId) return;

    if (this.state.expandedJobIds.includes(jobId)) {
      this.setState((prevState) => ({
        expandedJobIds: prevState.expandedJobIds.filter((id) => id !== jobId),
      }));
      return;
    }

    this.setState((prevState) => ({
      expandedJobIds: prevState.expandedJobIds.includes(jobId)
        ? prevState.expandedJobIds
        : [...prevState.expandedJobIds, jobId],
    }));
  };

  getRowHeight = (params) => {
    if (params.data && params.data.__isDetail) {
      const history = Array.isArray(params.data.jobHistory)
        ? params.data.jobHistory
        : [];
      const count = history.filter((entry) =>
        allowedHistoryTypes.has(normalizeType(entry.jobHistoryType))
      ).length;
      const rows = Math.max(count, 1);
      const headerHeight = 12;
      const rowHeight = 10;
      const padding = 0;
      return padding + headerHeight + rows * rowHeight;
    }
    return 20;
  };

  postSort = (rowNodes) => {
    if (!rowNodes || !rowNodes.length) return;

    const detailMap = {};
    const baseNodes = [];

    rowNodes.forEach((node) => {
      if (node.data && node.data.__isDetail) {
        detailMap[node.data.parentJobId] = node;
      } else {
        baseNodes.push(node);
      }
    });

    rowNodes.length = 0;
    baseNodes.forEach((node) => {
      rowNodes.push(node);
      const detailNode = detailMap[node.data?.jobId];
      if (detailNode) {
        rowNodes.push(detailNode);
      }
    });
  };

  getRowId = (params) => {
    if (params.data && params.data.__isDetail) {
      return `detail-${params.data.parentJobId}`;
    }
    if (params.data && params.data.jobId) {
      return `job-${params.data.jobId}`;
    }
    return params.id;
  };

  buildRowsWithDetails = () => {
    const baseRows = Array.isArray(this.props.workOrders)
      ? this.props.workOrders
      : [];
    const rows = [];

    baseRows.forEach((wo) => {
      rows.push(wo);
      if (this.state.expandedJobIds.includes(wo.jobId)) {
        rows.push({
          __isDetail: true,
          parentJobId: wo.jobId,
          jobHistory: this.state.jobHistoryCache[wo.jobId] || [],
          loading: this.state.loadingJobIds.includes(wo.jobId),
        });
      }
    });

    return rows;
  };

  onSortChanged = () => {
    if (!this.gridApi || typeof this.props.onSortedData !== 'function') {
      return;
    }

    const rowCount = this.gridApi.getDisplayedRowCount();
    const sortedRows = [];
    for (let i = 0; i < rowCount; i += 1) {
      const rowNode = this.gridApi.getDisplayedRowAtIndex(i);
      if (rowNode && rowNode.data && !rowNode.data.__isDetail) {
        sortedRows.push(rowNode.data);
      }
    }

    this.props.onSortedData(sortedRows);
  };

  render() {
    const { workOrders, handleCheckChange, onGridReady, ...props } = this.props;
    const rowsWithDetails = this.buildRowsWithDetails();

    const gridOptions = {
      suppressSizeToFit: true,
      columnDefs: columns,
      // Ensure Ag-Grid always receives an array to avoid rowData.map errors
      rowData: rowsWithDetails,
      context: {
        componentParent: this,
        handleCheckChange: handleCheckChange,
        gridApi: this.gridApi,
        handleJobNumClick: this.handleJobNumClick,
        handleOpenFollowUpJob: this.handleOpenFollowUpJob,
        checkboxStates: props.checkboxStates,
        addSchedule: props.addSchedule,
        setModal: props.setModal,
        setData: props.setData,
        setInterpreterEmail: props.setInterpreterEmail,
        onChange: handleCheckChange,
        state: props.state,
      },
      animateRows: true,
      defaultColDef: defaultColDef,
      components: {
        jobNumCellRenderer: CellRenderers.JobNumCellRenderer,
        repEmail2CellRenderer: CellRenderers.RepEmail2CellRenderer,
        addFollowUpCellRenderer: CellRenderers.AddFollowUpCellRenderer,
        profConfCellRenderer: CellRenderers.ProvConfCellRenderer,
        repAuthorizedCellRenderer: CellRenderers.RepAuthorizedCellRenderer,
        transportationCellRenderer: CellRenderers.TransportationCellRenderer,
        jobDateCellRenderer: CellRenderers.JobDateCellRenderer,
        claimantNameCellRenderer: CellRenderers.ClaimantNameCellRenderer,
        jobNotesCellRenderer: CellRenderers.JobNotesCellRenderer,
        intEmailCellRenderer: CellRenderers.IntEmailCellRenderer,
        intNameCellRenderer: CellRenderers.IntNameCellRenderer,
        repEmailCellRenderer: CellRenderers.RepEmailCellRenderer,
        providerEmailCellRenderer: CellRenderers.ProviderEmailCellRenderer,
        repNameCellRenderer: CellRenderers.RepNameCellRenderer,
        invoicedFlgCellRenderer: CellRenderers.InvoicedFlgCellRenderer,
        jobHistoryDetailRenderer: JobHistoryDetailRenderer,
      },
      rowSelection: 'multiple',
      onRowClicked: this.handleRowSingleClick,
      onColumnResized: props.onColumnResized,
      onSortChanged: this.onSortChanged,
      rowHeight: 20,
      getRowHeight: this.getRowHeight,
      getRowId: this.getRowId,
      isRowSelectable: (rowNode) => !rowNode.data?.__isDetail,
      getRowStyle: (params) =>
        params.data && params.data.__isDetail
          ? { backgroundColor: '#f4f5f6' }
          : null,
      postSort: this.postSort,
      tooltipShowDelay: 0,
      enableBrowserTooltips: true,
      onRowDoubleClicked: this.handleRowDoubleClick,
      onSelectionChanged: this.onSelectionChanged,
    };

    return (
      // 1) Outer flex container
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* 2) Flex item that fills the parent */}
        <div className="ag-theme-balham" style={{ flex: 1, width: '100%' }}>
          <AgGridReact {...gridOptions} onGridReady={this.onGridReady} />
        </div>
      </div>
    );
  }

  hasRelevantHistory = (history) => {
    const arr = Array.isArray(history) ? history : [];
    return arr.some((entry) =>
      allowedHistoryTypes.has(normalizeType(entry.jobHistoryType))
    );
  };

  expandAllRows = () => {
    const jobIds = Array.isArray(this.props.workOrders)
      ? this.props.workOrders
          .map((wo) => wo.jobId)
          .filter((id) => id !== null && id !== undefined)
      : [];

    if (!jobIds.length) {
      this.setState({ expandedJobIds: [], loadingJobIds: [] });
      return;
    }

    const nextExpandedFromCache = jobIds.filter((id) =>
      this.hasRelevantHistory(this.state.jobHistoryCache[id])
    );

    const nextExpanded = Array.from(new Set(nextExpandedFromCache));

    const existingIds = Array.isArray(this.state.expandedJobIds)
      ? this.state.expandedJobIds
      : [];

    const sameExpansion =
      nextExpanded.length === existingIds.length &&
      nextExpanded.every((id) => existingIds.includes(id));

    this.setState(
      (prevState) => ({
        expandedJobIds: sameExpansion ? prevState.expandedJobIds : nextExpanded,
        loadingJobIds: prevState.loadingJobIds.filter((id) =>
          jobIds.includes(id)
        ),
      })
    );
  };
}
